import { useEffect, useState } from 'react';

import { useMapGetBboxQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/map/get-bbox/query';
import { mapPreparedMapBboxSelector } from '@/packages/query-state/shared/store/search-offers-index/v2/map/get-bbox/selectors';
import { useGetYandexMapScriptQuery } from '@/packages/query-state/shared/store/yandex-map/2.1/get-yandex-map/query';
import { useFiltersBboxSelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/dumb';
import { useListingLayoutViewModeSelector } from '@/packages/redux-state/store/slices/listingLayout/selectors/dumb';
import { useMapConfigurationInitialStateSelector } from '@/packages/redux-state/store/slices/mapConfiguration';

import { Y_MAP_DEFAULT_STATE } from '../constants/map';
import { convertBboxToMapBounds } from '../utils/map/convertBboxToMapBounds';

interface IUseMapInitialStateParams {
  mapRef: React.RefObject<HTMLDivElement>;
  ymaps: YMaps.IYMaps | null;
}

export const useMapInitialState = ({ mapRef, ymaps }: IUseMapInitialStateParams): YMaps.IMapState | undefined => {
  const [mapState, setMapState] = useState<YMaps.IMapState>();

  const bbox = useFiltersBboxSelector();

  const viewMode = useListingLayoutViewModeSelector();

  const { center, zoom } = useMapConfigurationInitialStateSelector();

  const { isSuccess: isLoadMapScriptSuccess } = useGetYandexMapScriptQuery();

  const isMapInitialBboxQueryEnabled = !center && !zoom && !bbox && isLoadMapScriptSuccess;

  const { data: initialMapBbox, isSuccess } = useMapGetBboxQuery({
    enabled: isMapInitialBboxQueryEnabled,
    select: mapPreparedMapBboxSelector,
  });

  useEffect(() => {
    const mapElement = mapRef.current;

    if (!ymaps || !mapElement || mapState || viewMode === 'list') {
      return;
    }

    const { width, height } = mapElement.getBoundingClientRect();

    let state;

    if (center && zoom) {
      /** center и zoom пришли из queryString */
      state = { center, zoom };
    } else if (bbox) {
      /** bbox пришел из queryString */
      state = ymaps.util.bounds.getCenterAndZoom(convertBboxToMapBounds(bbox), [width, height]);
    } else if (isMapInitialBboxQueryEnabled && isSuccess) {
      /** bbox получили от бэка, если в квери нет параметров для центрирования */
      state = ymaps.util.bounds.getCenterAndZoom(initialMapBbox, [width, height]);
    } else if (!isMapInitialBboxQueryEnabled && !isSuccess) {
      /** стейт по умолчанию, если бэк не отработал */
      state = Y_MAP_DEFAULT_STATE;
    }

    if (state) {
      setMapState(state);
    }
  }, [initialMapBbox, ymaps, mapRef, mapState, viewMode, center, zoom, isSuccess, isMapInitialBboxQueryEnabled, bbox]);

  return mapState;
};
